import { useOrganization } from '@clerk/react';
import axios from 'axios';
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Company, Account } from '../../types/types';
import { useAccessToken } from '../../hooks/useAccessToken';

export interface UserInfoContextType {
  companyInfo: Company;
  accountInfo: Account;
  setUserInfo?: (updates: UserInfoUpdate | UserInfoUpdater) => void;
}

type UserInfoUpdate = Partial<
  Pick<UserInfoContextType, 'companyInfo' | 'accountInfo'>
>;

type UserInfoUpdater = (prev: UserInfoContextType) => UserInfoUpdate;

export const userInfoContext = createContext<UserInfoContextType>(null);

export default function UserInfoContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [value, setValue] = useState<UserInfoContextType>({
    companyInfo: null,
    accountInfo: null,
  });

  const { organization } = useOrganization();

  const { getToken } = useAccessToken();

  const setUserInfo = useCallback(
    (updates: UserInfoUpdate | UserInfoUpdater) =>
      setValue((prev) => ({
        ...prev,
        ...(typeof updates === 'function' ? updates(prev) : updates),
      })),
    [],
  );

  useEffect(() => {
    if (!organization?.publicMetadata?.accountId) return;
    (async () => {
      const token = await getToken();
      const { data: account } = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/account/${organization.publicMetadata.accountId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setValue({
        companyInfo: account.company,
        accountInfo: account,
      });
    })();
  }, [organization?.publicMetadata?.accountId]);

  const providedValue = useMemo(
    () => ({ ...value, setUserInfo }),
    [value, setUserInfo],
  );

  return (
    <userInfoContext.Provider value={providedValue}>
      {children}
    </userInfoContext.Provider>
  );
}
